import { type AgentRunDetail, isActiveAgentRunStatus } from '@fos/shared/contracts/agents';
import type { McpToolName } from '@fos/shared/contracts/mcp';
import {
  AGENT_RUN_COST_INFORMATIONAL_LABEL,
  AGENT_RUNTIME_LABELS,
  agentExecutorLabel,
  MCP_TOOL_LABELS,
} from '@fos/shared/labels';
import { Bot, Check, ExternalLink, Rocket, X } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router';
import { appErrorCode } from '../../../lib/api';
import { formatDateTimeShort, formatRelativePast } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import {
  Button,
  ContextBanner,
  EmptyState,
  ErrorState,
  Mono,
  Page,
  PageHeader,
  Section,
  SectionHeader,
  Skeleton,
} from '../../../ui';
import { StatusPill } from '../../../ui/StatusPill';
import { TOUCH_BUTTON, TOUCH_TEXT } from '../../mobile';
import { ModulePage } from '../../modules/pages/ModulePage';
import { AcceptRunDialog, CancelRunDialog, RequestDeployDialog } from '../components/RunDialogs';
import { ArtifactsList, ChangedFiles, RunApprovals, RunTimeline } from '../components/RunSections';
import {
  formatRunCost,
  formatRunDuration,
  runContext,
  runStatusLabel,
  runStatusTone,
  shortSha,
  testsLabel,
} from '../format';

/**
 * Szczegóły przebiegu (/agenci/:id; czeka na projekt w Figmie — wzorce Figmy 04 i 11): polecenie, wynik (podsumowanie,
 * testy, PR, branch, commit), zmienione pliki, artefakty z podglądem diffu, akceptacje, dziennik zdarzeń. Akcje:
 * „Odbierz wynik” (Primary), „Poproś o wdrożenie”, „Anuluj”. Run w toku odświeża się co 5 s.
 */
export function AgentRunPage() {
  const access = useAccess();
  const { id = '' } = useParams();
  const run = trpc.agents.get.useQuery(
    { id },
    {
      enabled: access.can('agents') && id.length > 0,
      refetchInterval: (q) => {
        const status = q.state.data?.status;
        return status === 'queued' || (status && isActiveAgentRunStatus(status)) ? 5000 : false;
      },
    },
  );
  if (!access.can('agents')) return <ModulePage module="agents" />;
  if (run.isLoading) {
    return (
      <Page width="reading">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="mt-4 h-8 w-2/3" />
        <Skeleton className="mt-8 h-32 w-full" />
      </Page>
    );
  }
  if (run.isError || !run.data) {
    const notFound = appErrorCode(run.error) === 'NOT_FOUND';
    return (
      <Page width="reading">
        <Section>
          {notFound ? (
            <EmptyState
              icon={Bot}
              title="Nie ma takiego przebiegu"
              description="Mógł zostać usunięty albo jest poza Twoim zakresem."
              action={
                <Button asChild variant="secondary" className={TOUCH_BUTTON}>
                  <Link to="/agenci">Wróć do Agentów</Link>
                </Button>
              }
            />
          ) : (
            <ErrorState onRetry={() => void run.refetch()} retrying={run.isFetching} />
          )}
        </Section>
      </Page>
    );
  }
  return <RunDetail run={run.data} />;
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-h-10 items-baseline justify-between gap-4 border-b border-line py-2">
      <dt className="shrink-0 text-body-s text-fg-muted">{label}</dt>
      <dd className="min-w-0 text-right text-body-s break-words text-fg">{children}</dd>
    </div>
  );
}

function RunDetail({ run }: { run: AgentRunDetail }) {
  const access = useAccess();
  const location = useLocation();
  const [accepting, setAccepting] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const showCost = access.flag('seesRates');
  const tests = testsLabel(run.tests);

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) document.getElementById(hash)?.scrollIntoView({ block: 'start' });
  }, [location.hash]);

  const meta = (
    <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
      <StatusPill tone={runStatusTone(run)}>{runStatusLabel(run)}</StatusPill>
      <span>{agentExecutorLabel(run)}</span>
      {runContext(run) && <span>{runContext(run)}</span>}
      <Mono className="text-caption">{formatRunDuration(run.durationSec)}</Mono>
      {showCost && run.costPln !== undefined && (
        <span>
          <Mono className="text-caption">{formatRunCost(run.costPln)}</Mono>
          {run.costInformational && Number(run.costPln) > 0 && (
            <span className="text-caption text-fg-muted"> · {AGENT_RUN_COST_INFORMATIONAL_LABEL}</span>
          )}
        </span>
      )}
    </span>
  );

  const actions = (
    <>
      {run.permissions.cancel && (
        <Button variant="ghost" icon={X} onClick={() => setCancelling(true)} className={TOUCH_BUTTON}>
          Anuluj
        </Button>
      )}
      {run.permissions.requestDeploy && (
        <Button
          variant={run.permissions.accept ? 'secondary' : 'primary'}
          icon={Rocket}
          onClick={() => setDeploying(true)}
          className={TOUCH_BUTTON}
        >
          Poproś o wdrożenie
        </Button>
      )}
      {run.permissions.accept && (
        <Button variant="primary" icon={Check} onClick={() => setAccepting(true)} className={TOUCH_BUTTON}>
          Odbierz wynik
        </Button>
      )}
    </>
  );

  return (
    <Page width="reading">
      <PageHeader
        detail
        breadcrumbs={[{ label: 'Agenci', to: '/agenci' }, { label: `Przebieg #${run.number}` }]}
        title={run.title}
        meta={meta}
        actions={actions}
      />

      {run.cancelRequestedAt && isActiveAgentRunStatus(run.status) && (
        <ContextBanner tone="warning" className="mt-6">
          Poproszono o anulowanie {formatRelativePast(run.cancelRequestedAt)} — runner przerwie pracę. Wykonane kroki
          nie są cofane.
        </ContextBanner>
      )}
      {run.status === 'failed' && run.failureReason && (
        <ContextBanner tone="warning" className="mt-6">
          {run.failureReason}
        </ContextBanner>
      )}

      <Section aria-labelledby="polecenie" className="mt-6">
        <SectionHeader title={<span id="polecenie">Polecenie</span>} />
        <p className="text-body-m whitespace-pre-wrap text-fg">{run.instruction}</p>
        {run.task && (
          <p className="mt-2 text-body-s text-fg-muted">
            Zadanie{' '}
            <Link to={`/zadania/${run.task.id}`} className={`text-accent hover:underline ${TOUCH_TEXT}`}>
              #{run.task.number} {run.task.title}
            </Link>
          </p>
        )}
      </Section>

      {(run.summary || tests || run.prUrl || run.commitSha) && (
        <Section aria-labelledby="wynik" className="mt-4 md:mt-6">
          <SectionHeader title={<span id="wynik">Wynik</span>} />
          {run.summary && <p className="text-body-m whitespace-pre-wrap text-fg">{run.summary}</p>}
          <dl className="mt-3">
            {tests && (
              <Fact label="Testy">
                {tests}
                {run.tests?.command && <Mono className="ml-2 text-caption text-fg-muted">{run.tests.command}</Mono>}
              </Fact>
            )}
            {run.prUrl && (
              <Fact label="Pull request">
                <a
                  href={run.prUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-1 text-accent hover:underline ${TOUCH_TEXT}`}
                >
                  {run.prUrl.replace(/^https?:\/\//, '')}
                  <ExternalLink size={12} strokeWidth={1.75} aria-hidden />
                </a>
              </Fact>
            )}
            {run.branch && (
              <Fact label="Branch">
                <Mono className="text-caption">{run.branch}</Mono>
              </Fact>
            )}
            {run.commitSha && (
              <Fact label="Commit">
                <Mono className="text-caption" title={run.commitSha}>
                  {shortSha(run.commitSha)}
                </Mono>
              </Fact>
            )}
          </dl>
          {run.tests?.outputExcerpt && (
            <pre className="mt-3 max-h-56 overflow-auto rounded-lg bg-surface p-3 font-mono text-[12px] leading-[1.55] whitespace-pre-wrap text-fg-secondary">
              {run.tests.outputExcerpt}
            </pre>
          )}
        </Section>
      )}

      <Section aria-labelledby="zmiany-tytul" id="zmiany" className="mt-4 scroll-mt-6 md:mt-6">
        <SectionHeader title={<span id="zmiany-tytul">Zmienione pliki</span>} />
        <ChangedFiles files={run.changedFiles} />
      </Section>

      <Section aria-labelledby="artefakty" className="mt-4 md:mt-6">
        <SectionHeader title={<span id="artefakty">Artefakty</span>} />
        <ArtifactsList artifacts={run.artifacts} />
      </Section>

      <RunApprovals approvals={run.approvals} />

      <Section aria-labelledby="dziennik-tytul" id="dziennik" className="mt-4 scroll-mt-6 md:mt-6">
        <SectionHeader title={<span id="dziennik-tytul">Dziennik</span>} />
        <RunTimeline events={run.events} truncated={run.eventsTruncated} />
      </Section>

      <Section aria-labelledby="szczegoly" className="mt-4 md:mt-6">
        <SectionHeader title={<span id="szczegoly">Szczegóły</span>} />
        <dl>
          <Fact label="Wykonawca">{AGENT_RUNTIME_LABELS[run.runtime]}</Fact>
          {run.runner && <Fact label="Komputer">{run.runner.name}</Fact>}
          {run.model && <Fact label="Model">{run.model}</Fact>}
          {run.repoUrl && (
            <Fact label="Repozytorium">
              <Mono className="text-caption break-all">{run.repoUrl}</Mono>
            </Fact>
          )}
          {run.baseBranch && (
            <Fact label="Branch bazowy">
              <Mono className="text-caption">{run.baseBranch}</Mono>
            </Fact>
          )}
          <Fact label="Limity">
            {run.limits.maxMinutes} min · {formatRunCost(run.limits.maxCostPln)} · {run.limits.maxTurns} kroków
            {run.runtime === 'local_runner' && (
              <span className="block text-caption text-fg-muted">
                Limit kosztu działa tylko przy płatnym API; przy subskrypcji Claude ogranicza czas i kroki.
              </span>
            )}
          </Fact>
          <Fact label="Narzędzia MCP">
            {run.allowedTools.map((t) => MCP_TOOL_LABELS[t as McpToolName] ?? t).join(', ') || '—'}
          </Fact>
          {run.contextVersion && (
            <Fact label="Wersja kontekstu">
              <Mono className="text-caption" title={run.contextVersion}>
                {run.contextVersion.slice(0, 19)}
              </Mono>
            </Fact>
          )}
          <Fact label="Zlecił">
            {run.createdBy.name}, {formatDateTimeShort(run.createdAt)}
          </Fact>
          {run.acceptedBy && run.acceptedAt && (
            <Fact label="Odebrał">
              {run.acceptedBy.name}, {formatDateTimeShort(run.acceptedAt)}
            </Fact>
          )}
        </dl>
      </Section>

      <AcceptRunDialog run={run} open={accepting} onOpenChange={setAccepting} />
      <RequestDeployDialog run={run} open={deploying} onOpenChange={setDeploying} />
      <CancelRunDialog run={run} open={cancelling} onOpenChange={setCancelling} />
    </Page>
  );
}
